import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, FileArchive, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

import {
  enviarLote,
  lerZip,
  progressoDaFila,
  type ProblemaNoZip,
  type SiteDoZip,
} from '../lib/importacao-massa';

/**
 * ============================================================================
 * IMPORTAÇÃO DE SITES EM MASSA
 * ============================================================================
 *
 * Recebe os ZIPs exportados pela ferramenta externa — uma pasta por site, com
 * `index.html`, `data.json` e `system_meta.json` — e entrega o trabalho pesado
 * ao worker.
 *
 * A TELA TEM TRÊS ESTADOS, E ELES SÃO DIFERENTES DE PROPÓSITO
 *
 *   1. **Lendo** os ZIPs: acontece no navegador, é rápido, e serve para a
 *      pessoa CONFERIR o que vai importar antes de mandar. Um ZIP com pastas
 *      erradas aparece aqui, não depois de cinco mil jobs enfileirados.
 *   2. **Enviando**: sobe os HTMLs para o R2 e enfileira. É a única parte que
 *      exige a aba aberta, e fechar no meio só interrompe o ENVIO — o que já
 *      foi enfileirado continua sendo processado.
 *   3. **Fila**: o worker trabalhando. A aba pode fechar. Voltar aqui depois
 *      mostra o andamento de onde parou, porque o número vem do servidor.
 */

type Fase = 'ocioso' | 'lendo' | 'pronto' | 'enviando' | 'enviado';

export const ImportacaoEmMassa: React.FC = () => {
  const [fase, setFase] = useState<Fase>('ocioso');
  const [sites, setSites] = useState<SiteDoZip[]>([]);
  const [problemas, setProblemas] = useState<ProblemaNoZip[]>([]);
  const [lote, setLote] = useState('');
  const [andamento, setAndamento] = useState({ enviados: 0, total: 0, atual: '' });
  const [resultado, setResultado] = useState<{ enfileirados: number; jaExistiam: number } | null>(
    null,
  );
  const [falhas, setFalhas] = useState<Array<{ pasta: string; motivo: string }>>([]);
  const [fila, setFila] = useState<Awaited<ReturnType<typeof progressoDaFila>> | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const arquivoRef = useRef<HTMLInputElement>(null);

  /*
    O andamento da fila vem do SERVIDOR, a cada 5 s.

    Não é um contador local: o worker roda fora desta aba, e um número mantido
    aqui pararia no instante em que a pessoa recarregasse. Vindo do servidor,
    fechar e voltar mostra onde a importação realmente está.
  */
  const atualizarFila = useCallback(async () => {
    try {
      setFila(await progressoDaFila());
    } catch (e) {
      console.warn('[importacao] não consegui ler o andamento da fila:', e);
    }
  }, []);

  useEffect(() => {
    void atualizarFila();
    const t = setInterval(() => void atualizarFila(), 5000);
    return () => clearInterval(t);
  }, [atualizarFila]);

  const receberArquivos = async (lista: File[]) => {
    const zips = lista.filter((f) => f.name.toLowerCase().endsWith('.zip'));
    if (zips.length === 0) {
      toast.error('Arraste arquivos .zip exportados pela ferramenta.');
      return;
    }

    setFase('lendo');
    setSites([]);
    setProblemas([]);
    setResultado(null);
    setFalhas([]);

    const achados: SiteDoZip[] = [];
    const ruins: ProblemaNoZip[] = [];
    for (const zip of zips) {
      try {
        const r = await lerZip(zip);
        achados.push(...r.sites);
        ruins.push(...r.problemas);
      } catch (e) {
        console.error(`[importacao] falha ao abrir "${zip.name}":`, e);
        ruins.push({ pasta: zip.name, motivo: 'não consegui abrir o ZIP' });
      }
    }

    setSites(achados);
    setProblemas(ruins);
    setLote(sugerirLote(zips));
    setFase(achados.length ? 'pronto' : 'ocioso');
    if (!achados.length) toast.error('Nenhuma pasta com index.html nesses arquivos.');
  };

  const enviar = async () => {
    setFase('enviando');
    try {
      const r = await enviarLote(lote.trim() || sugerirLote([]), sites, setAndamento);
      setResultado({ enfileirados: r.enfileirados, jaExistiam: r.jaExistiam });
      setFalhas(r.falhas);
      setFase('enviado');
      await atualizarFila();
      toast.success(
        `${r.enfileirados} site(s) na fila. O worker publica em segundo plano — pode fechar esta aba.`,
      );
    } catch (e) {
      console.error('[importacao] falha no envio:', e);
      toast.error(e instanceof Error ? e.message : 'Falha ao enviar o lote.');
      setFase('pronto');
    }
  };

  const totalNaFila = fila
    ? fila.pendente + fila.processando + fila.concluido + fila.falhou
    : 0;
  const pct = totalNaFila ? Math.round((fila!.concluido / totalNaFila) * 100) : 0;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">
          Importação de sites em massa
        </h1>
        <p className="mt-1 text-sm leading-relaxed text-gray-500">
          Arraste os ZIPs exportados pela ferramenta externa. Cada pasta com{' '}
          <code className="rounded bg-gray-100 px-1 font-mono text-xs">index.html</code> vira um
          cliente com o site publicado. As imagens em base64 dentro do HTML são extraídas para o
          R2 e religadas por link — o worker faz isso em segundo plano.
        </p>
      </header>

      {/* --------------------------------------------------------- soltar --- */}
      {(fase === 'ocioso' || fase === 'lendo') && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setArrastando(true);
          }}
          onDragLeave={() => setArrastando(false)}
          onDrop={(e) => {
            e.preventDefault();
            setArrastando(false);
            void receberArquivos(Array.from(e.dataTransfer.files));
          }}
          onClick={() => arquivoRef.current?.click()}
          className={`cursor-pointer rounded-2xl border-2 border-dashed p-12 text-center transition-colors ${
            arrastando ? 'border-gray-900 bg-gray-50' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          {fase === 'lendo' ? (
            <>
              <Loader2 className="mx-auto animate-spin text-gray-400" size={32} />
              <p className="mt-3 text-sm text-gray-600">Lendo os ZIPs…</p>
            </>
          ) : (
            <>
              <FileArchive className="mx-auto text-gray-400" size={32} />
              <p className="mt-3 text-sm font-medium text-gray-900">
                Arraste os ZIPs aqui, ou clique para escolher
              </p>
              <p className="mt-1 text-xs text-gray-500">
                Pode soltar vários de uma vez. Nada é enviado antes de você conferir.
              </p>
            </>
          )}
          <input
            ref={arquivoRef}
            type="file"
            accept=".zip,application/zip"
            multiple
            hidden
            onChange={(e) => {
              const l = e.target.files;
              const fs: File[] = l ? Array.from({ length: l.length }, (_, i) => l[i]!) : [];
              void receberArquivos(fs);
              if (arquivoRef.current) arquivoRef.current.value = '';
            }}
          />
        </div>
      )}

      {/* ------------------------------------------------- o que foi lido --- */}
      {(fase === 'pronto' || fase === 'enviando' || fase === 'enviado') && (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <header className="flex items-center gap-4 border-b border-gray-100 px-5 py-4">
            <div className="mr-auto">
              <h2 className="text-sm font-semibold text-gray-900">
                {sites.length} site{sites.length === 1 ? '' : 's'} pronto
                {sites.length === 1 ? '' : 's'} para importar
              </h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Confira antes de enviar. Nada foi para o servidor ainda.
              </p>
            </div>

            {fase === 'pronto' && (
              <>
                <label className="text-xs text-gray-500">
                  Lote
                  <input
                    value={lote}
                    onChange={(e) => setLote(e.target.value)}
                    className="ml-2 w-44 rounded-lg border border-gray-200 px-2 py-1.5 font-mono text-xs text-gray-900 outline-none focus:border-gray-400"
                  />
                </label>
                <button
                  onClick={() => void enviar()}
                  className="flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-black"
                >
                  <Upload size={15} />
                  Importar {sites.length}
                </button>
                <button
                  onClick={() => {
                    setFase('ocioso');
                    setSites([]);
                    setProblemas([]);
                  }}
                  aria-label="Descartar"
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-900"
                >
                  <X size={16} />
                </button>
              </>
            )}
          </header>

          {fase === 'enviando' && (
            <div className="border-b border-gray-100 px-5 py-4">
              <div className="flex items-center justify-between text-xs text-gray-600">
                <span className="flex items-center gap-2">
                  <Loader2 size={13} className="animate-spin" />
                  Enviando {andamento.enviados} de {andamento.total}
                  {andamento.atual && <span className="text-gray-400">· {andamento.atual}</span>}
                </span>
                <span className="font-mono">
                  {Math.round((andamento.enviados / Math.max(1, andamento.total)) * 100)}%
                </span>
              </div>
              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full bg-gray-900 transition-all"
                  style={{
                    width: `${(andamento.enviados / Math.max(1, andamento.total)) * 100}%`,
                  }}
                />
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                Só o envio precisa da aba aberta. Depois disso o worker continua sozinho.
              </p>
            </div>
          )}

          {resultado && (
            <p className="flex items-start gap-2 border-b border-green-200 bg-green-50 px-5 py-3 text-xs leading-relaxed text-green-800">
              <CheckCircle2 size={14} className="mt-0.5 shrink-0" />
              <span>
                <b>{resultado.enfileirados} na fila.</b>
                {resultado.jaExistiam > 0 && (
                  <> {resultado.jaExistiam} já tinham sido enviados neste lote e foram ignorados.</>
                )}{' '}
                Pode fechar a aba: o worker publica em segundo plano.
              </span>
            </p>
          )}

          <ul className="max-h-72 divide-y divide-gray-50 overflow-y-auto">
            {sites.slice(0, 300).map((s) => (
              <li key={s.pasta} className="flex items-center gap-3 px-5 py-2.5 text-xs">
                <span className="flex-1 truncate font-medium text-gray-900">{s.cliente.nome}</span>
                <span className="truncate font-mono text-[11px] text-gray-400">{s.pasta}</span>
                <span className="shrink-0 tabular-nums text-gray-400">
                  {Math.round(s.bytes / 1024)} KB
                </span>
              </li>
            ))}
            {sites.length > 300 && (
              <li className="px-5 py-2.5 text-xs text-gray-400">
                …e mais {sites.length - 300}. Todos serão importados; a lista só mostra os 300
                primeiros.
              </li>
            )}
          </ul>
        </section>
      )}

      {/* --------------------------------------------------- o que falhou --- */}
      {(problemas.length > 0 || falhas.length > 0) && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="flex items-center gap-2 text-xs font-semibold text-amber-900">
            <AlertCircle size={14} />
            {problemas.length + falhas.length} pasta(s) fora da importação
          </h3>
          <ul className="mt-2 space-y-1 text-xs text-amber-800">
            {[...problemas, ...falhas].slice(0, 20).map((p, i) => (
              <li key={`${p.pasta}-${i}`}>
                <span className="font-mono">{p.pasta}</span> — {p.motivo}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ------------------------------------------------ andamento da fila --- */}
      {fila && totalNaFila > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">O worker está publicando</h2>
            <span className="font-mono text-xs text-gray-500">
              {fila.concluido} de {totalNaFila}
            </span>
          </div>

          <div className="mt-3 h-2 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full bg-green-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-3 text-xs sm:grid-cols-4">
            <Numero rotulo="Na fila" valor={fila.pendente} />
            <Numero rotulo="Publicando" valor={fila.processando} destaque={fila.processando > 0} />
            <Numero rotulo="Publicados" valor={fila.concluido} />
            <Numero rotulo="Falharam" valor={fila.falhou} alerta={fila.falhou > 0} />
          </dl>

          <p className="mt-4 border-t border-gray-100 pt-3 text-[11px] leading-relaxed text-gray-400">
            O número vem do servidor, não desta aba: fechar e voltar mostra onde a importação
            realmente está. Um site que falhe é tentado 3 vezes antes de sair da fila.
          </p>
        </section>
      )}
    </div>
  );
};

const Numero: React.FC<{
  rotulo: string;
  valor: number;
  destaque?: boolean;
  alerta?: boolean;
}> = ({ rotulo, valor, destaque, alerta }) => (
  <div className="rounded-lg bg-gray-50 px-3 py-2">
    <dt className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{rotulo}</dt>
    <dd
      className={`mt-0.5 text-lg font-bold tabular-nums ${
        alerta ? 'text-red-600' : destaque ? 'text-blue-600' : 'text-gray-900'
      }`}
    >
      {valor}
    </dd>
  </div>
);

/**
 * Um rótulo de lote legível, derivado do nome do ZIP.
 *
 * Sem data: `new Date()` aqui daria dois rótulos diferentes para o mesmo ZIP
 * reenviado, e a chave de idempotência é `lote + pasta` — o lote mudando faria
 * o reenvio duplicar tudo, que é justamente o que a idempotência existe para
 * impedir.
 */
function sugerirLote(zips: File[]): string {
  const base = zips[0]?.name.replace(/\.zip$/i, '') ?? 'importacao';
  return base
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'importacao';
}
